import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, map, tap } from 'rxjs';
import {
  ApiResponsePaginated,
  OrganizationDetailModel,
  OrganizationModel,
  OrganizationQueryParams,
  ParentChildOrganization,
  UserResultModel,
} from './organization.interface';
import { environment } from '../../../environments/environment';
import { ErrorHandlingService } from '../../core/services/error-handling.service';
import { ApiResponse } from '../../core/models/api-response.models';
import { ActivatedRoute } from '@angular/router';
import {
  UploadProgressService,
  UploadEvent,
} from '../../core/services/upload-progress.service';
import { ApiResponses } from '../../core/models/common.models';

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  private uploadProgressService = inject(UploadProgressService);
  private readonly BASE_URL = environment.apiUrl;
  private readonly ORGANIZATION_URL = `${this.BASE_URL}/Organization`;
  private readonly NEW_ORGANIZATION_URL = `${this.BASE_URL}/organization`;

  private readonly CONTACT_URL = `${this.BASE_URL}/Contact`;
  private readonly USER_URL = `${this.BASE_URL}/User`;

  private readonly http = inject(HttpClient);
  private readonly errorHandler = inject(ErrorHandlingService);
  private readonly route = inject(ActivatedRoute);

  private readonly organizationsSubject =
    new BehaviorSubject<ApiResponsePaginated | null>(null);
  private readonly organizationsChildSubject =
    new BehaviorSubject<ApiResponsePaginated | null>(null);
  private readonly localAdminsSubject = new BehaviorSubject<ApiResponse<
    string[]
  > | null>(null);

  readonly organizations$ = this.organizationsSubject.asObservable();
  readonly organizationsChild$ = this.organizationsChildSubject.asObservable();
  readonly localadmins$ = this.localAdminsSubject.asObservable();

  getOrganizations(
    queryParams: OrganizationQueryParams
  ): Observable<ApiResponsePaginated> {
    this.organizationsSubject.next(null);
    return this.http
      .get<ApiResponsePaginated>(`${this.ORGANIZATION_URL}/list`, {
        params: this.buildQueryParams(queryParams),
      })
      .pipe(
        tap(response => this.organizationsSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getOrganizationsLookup(
    queryParams: OrganizationQueryParams
  ): Observable<ApiResponsePaginated> {
    return this.http
      .get<ApiResponsePaginated>(`${this.ORGANIZATION_URL}/list`, {
        params: this.buildQueryParams(queryParams),
      })
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getChildOrganizations(
    queryParams: OrganizationQueryParams
  ): Observable<ApiResponsePaginated> {
    this.organizationsChildSubject.next(null);
    return this.http
      .get<ApiResponsePaginated>(`${this.ORGANIZATION_URL}/list`, {
        params: this.buildQueryParams(queryParams),
      })
      .pipe(
        tap(response => this.organizationsChildSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getOrganization(id: number): Observable<OrganizationModel> {
    return this.http
      .get<ApiResponse<OrganizationModel>>(`${this.ORGANIZATION_URL}/${id}`)
      .pipe(
        map(response => {
          if (response.result === null) {
            throw new Error('Organization not found');
          }
          return response.result;
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  createOrganization(
    organization: OrganizationModel
  ): Observable<ApiResponse<OrganizationModel>> {
    return this.http
      .post<
        ApiResponse<OrganizationModel>
      >(`${this.ORGANIZATION_URL}/create`, organization)
      .pipe(
        tap(() => this.refreshOrganizations()),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  updateOrganization(
    id: number,
    organization: OrganizationModel
  ): Observable<ApiResponse<OrganizationModel>> {
    return this.http
      .put<ApiResponse<OrganizationModel>>(
        `${this.ORGANIZATION_URL}/update`,
        organization,
        {
          params: { organizationId: id.toString() },
        }
      )
      .pipe(
        tap(() => this.refreshOrganizations()),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  deleteOrganization(id: number): Observable<ApiResponse<void>> {
    return this.http
      .delete<ApiResponse<void>>(`${this.ORGANIZATION_URL}/${id}`)
      .pipe(
        tap(() => this.refreshOrganizations()),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getContactsByOrgId(
    organizationId: number
  ): Observable<ApiResponse<UserResultModel>> {
    return this.http
      .get<ApiResponse<UserResultModel>>(`${this.CONTACT_URL}/get-by-org-id`, {
        params: { organizationId: organizationId.toString() },
      })
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  createContact(
    contact: UserResultModel
  ): Observable<ApiResponse<UserResultModel>> {
    return this.http
      .post<ApiResponse<UserResultModel>>(`${this.CONTACT_URL}/create`, contact)
      .pipe(
        tap(() => this.refreshOrganizations()),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  updateContact(
    id: number,
    contact: UserResultModel
  ): Observable<ApiResponse<UserResultModel>> {
    return this.http
      .put<ApiResponse<UserResultModel>>(
        `${this.CONTACT_URL}/update`,
        contact,
        {
          params: { contactId: id.toString() },
        }
      )
      .pipe(
        tap(() => this.refreshOrganizations()),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  deleteContact(id: number): Observable<ApiResponse<void>> {
    return this.http
      .delete<ApiResponse<void>>(`${this.CONTACT_URL}/${id}`)
      .pipe(
        tap(() => this.refreshOrganizations()),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  bulkCreateOrganizations(
    file: File,
    parentOrganizationId: number
  ): Observable<UploadEvent<void>> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http
      .post<ApiResponse<void>>(
        `${this.ORGANIZATION_URL}/bulk-create`,
        formData,
        {
          params: { parentOrganizationId: parentOrganizationId.toString() },
          reportProgress: true,
          observe: 'events',
        }
      )
      .pipe(
        this.uploadProgressService.getUploadProgress<void>(),
        tap(response => {
          if (response.status === 'completed') {
            this.refreshOrganizations();
          }
        })
      );
  }

  uploadRoster(
    organizationFile: File,
    userFile: File
  ): Observable<UploadEvent<void>> {
    const formData = new FormData();
    formData.append('OneRosterOrganizationFile', organizationFile);
    formData.append('OneRosterUserFile', userFile);

    return this.http
      .post<ApiResponse<void>>(`${this.BASE_URL}/Roster/upload`, formData, {
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        this.uploadProgressService.getUploadProgress<void>(),
        tap(response => {
          if (response.status === 'completed') {
            this.refreshOrganizations();
          }
        })
      );
  }

  getLocalAdmins(payload: {
    sortOrder: number | undefined;
    organizationId: number;
  }): Observable<ApiResponse<string[]>> {
    this.localAdminsSubject.next(null);
    const params = this.buildAdminQueryParams(payload.sortOrder);

    return this.http
      .get<
        ApiResponse<string[]>
      >(`${this.USER_URL}/get-local-admin-by-org-id/${payload.organizationId}`, { params })
      .pipe(
        tap(response => this.localAdminsSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  private buildAdminQueryParams(sortOrder: number | undefined): HttpParams {
    let params = new HttpParams();

    if (sortOrder !== undefined) {
      params = params.set('sortOrder', sortOrder.toString());
    }
    return params;
  }

  private buildQueryParams({
    pageIndex,
    pageSize,
    sortOrder,
    sortField,
    searchTerm,
    selectedInitials,
    parentOrganizationId,
  }: OrganizationQueryParams): HttpParams {
    let params = new HttpParams()
      .set('Page', pageIndex.toString())
      .set('PageSize', pageSize.toString());

    if (selectedInitials?.length) {
      params = params.set('selectedInitials', selectedInitials.join(','));
    }

    if (sortField) {
      params = params.set('sort', sortField);
    }

    if (sortOrder !== undefined) {
      params = params.set('sortOrder', sortOrder.toString());
    }
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }
    if (parentOrganizationId) {
      params = params.set(
        'parentOrganizationId',
        parentOrganizationId.toString()
      );
    }

    return params;
  }
  refreshOrganizations(): void {
    const page = this.route.snapshot.queryParams['page'] || 1;
    const search = this.route.snapshot.queryParams['search'] || '';
    this.organizationsSubject.next(null);
    this.getOrganizations({
      pageIndex: page,
      pageSize: 10,
      searchTerm: search,
    }).subscribe();
  }

  getOrganizationsToSwitch() {
    return this.http
      .get<
        ApiResponses<OrganizationModel>
      >(`${this.ORGANIZATION_URL}/get-organizations-to-switch`)
      .pipe(
        map(response => {
          return response;
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getOrganizationsForSelect(searchInput: string) {
    return this.http
      .get<
        ApiResponses<ParentChildOrganization>
      >(`${this.ORGANIZATION_URL}/get-organization-select-list?searchTerm=${searchInput}`)
      .pipe(
        map(response => {
          return response;
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  switchOrganization(
    orgId: number
  ): Observable<ApiResponse<OrganizationDetailModel>> {
    return this.http
      .post<
        ApiResponse<OrganizationDetailModel>
      >(`${this.ORGANIZATION_URL}/switch-organization?organizationId=${orgId}`, {})
      .pipe(
        tap((response: ApiResponse<OrganizationDetailModel>) => {
          return response;
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  triggerSftpUpload(organizationId: number): Observable<ApiResponse<void>> {
    return this.http
      .get<ApiResponse<void>>(`${this.BASE_URL}/roster/sftp-upload`, {
        params: { OrgnizationId: organizationId.toString() },
      })
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getSftpStatus(
    organizationId: number
  ): Observable<ApiResponse<{ isSftpEnabled: boolean }>> {
    return this.http
      .get<ApiResponse<{ isSftpEnabled: boolean }>>(
        `${this.BASE_URL}/sftp/getsftpstatus`,
        {
          params: { organizationId: organizationId.toString() },
        }
      )
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getOrganizationsByUserId(
    userId: number
  ): Observable<ApiResponse<OrganizationModel[]>> {
    return this.http
      .get<
        ApiResponse<OrganizationModel[]>
      >(`${this.NEW_ORGANIZATION_URL}/get-organizations-by-user-id?UserId=${userId}`)
      .pipe(
        map(response => {
          return response;
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }
}
